import { fetchAddAddress, fetchUpdateAddress } from '@/api/account';
import { Address, Country } from '@/api/types/public';
import AddressForm from '@/components/Address';
import Button from '@/components/Button';
import MobileNav from '@/components/MobileNav';
import Popup from '@/components/Popup';
import logger from '@/utils/client-logger';
import { env } from '@/utils/env';
import { useState } from 'react';
import { AddressActionType } from '../../index';
import AddressCard from './AddressCard';
import { useTranslations } from 'next-intl';

interface ChooseAddressProps {
  open: boolean;
  onClose: () => void;
  addressList: Address[];
  countryList: Country[];
  onChooseAddress: (address: Address) => void;
  onUpdateAddressList: (id?: number) => void;
  onTrackEvent: (event: string) => void;
  onEditTrackEvent: (event: string, is_save_success: boolean) => void;
}

export default function ChooseAddress({
  open,
  onClose,
  addressList,
  countryList,
  onChooseAddress,
  onUpdateAddressList,
  onTrackEvent,
  onEditTrackEvent,
}: ChooseAddressProps) {
  const t = useTranslations('payment.address');

  const defaultAddress = {
    firstname: '',
    lastname: '',
    country_id: env.NEXT_PUBLIC_ADDRESS_COUNTRY_ID,
    country: env.NEXT_PUBLIC_ADDRESS_COUNTRY,
    street: '',
    apartment: '',
    city: '',
    province: '',
    province_id: '',
    region_code: '',
    postcode: '',
    tel_code: env.NEXT_PUBLIC_ADDRESS_TEL_CODE,
    tel_country: env.NEXT_PUBLIC_ADDRESS_TEL_COUNTRY,
    telephone: '',
    cpf: '', // 巴西税号
    default: 2,
  };

  const editClick = async (address: Address) => {
    setActionType(AddressActionType.EDIT);
    setAddressFormData(address);
    setOpenAddNewAddress(true);
    onTrackEvent('编辑');
  };
  const chooseAddress = (address: Address) => {
    onChooseAddress(address);
    onClose();
  };
  const addNewAddress = () => {
    setActionType(AddressActionType.ADD);
    setAddressFormData(defaultAddress);
    setOpenAddNewAddress(true);
    onTrackEvent('Add New Address');
  };

  const [openAddNewAddress, setOpenAddNewAddress] = useState(false);
  const [loading, setLoading] = useState(false);
  const [addressFormData, setAddressFormData] = useState<Address>({});
  const [actionType, setActionType] = useState<'ADD' | 'EDIT'>(AddressActionType.ADD);

  const handleOnSubmit = async (address: Address) => {
    setLoading(true);
    logger.log(loading);

    let handleId = 0;
    if (actionType === AddressActionType.ADD) {
      // 新增
      const { code, data } = await fetchAddAddress(address);
      if (code !== 200) {
        onEditTrackEvent('Save', false);

        setLoading(false);
        return;
      }
      handleId = data?.id || 0;
    } else {
      // 修改
      const { code } = await fetchUpdateAddress(address.id, address);
      if (code !== 200) {
        onEditTrackEvent('Save', false);
        setLoading(false);
        return;
      }
      handleId = address.id;
    }
    onEditTrackEvent('Save', true);
    onUpdateAddressList(handleId);

    setOpenAddNewAddress(false);
    setLoading(false);
  };

  const handleClose = () => {
    onEditTrackEvent('Cancel', false);
    setOpenAddNewAddress(false);
  };

  return (
    <Popup
      open={open}
      onClose={onClose}
      position="right"
      height="100%"
      width="100%"
      sx={{
        '& .MuiDrawer-paper': {
          borderRadius: '0',
          width: '100%',
          backgroundColor: '#f5f6f7',
        },
        '& .MuiBox-root': {
          padding: '0',
          maxHeight: '100vh',
        },
      }}
    >
      <div>
        <MobileNav
          className="!absolute top-0 left-0 w-full z-20 bg-[#f5f6f7]"
          titleClassName="text-center w-full text-[18px] !font-bb"
          title={t('myAddress')}
          goBack={onClose}
        />

        <div className="px-12 mb-[80px] pt-[50px]">
          {addressList.map((item) => (
            <AddressCard
              key={item.id}
              address={item}
              countryList={countryList}
              onEditClick={editClick}
              onChooseAddress={chooseAddress}
            />
          ))}
        </div>

        <div className="absolute bottom-0 left-0 w-full h-[55px] bg-white flex justify-center items-center">
          <Button
            variant="contained"
            onClick={addNewAddress}
            sx={{
              width: '350px',
              height: '45px',
              fontSize: '16px',
              borderRadius: '34px',
              fontFamily: 'var(--font-bricolage-bold)',
            }}
          >
            {t('addNewAddress')}
          </Button>
        </div>
      </div>

      <Popup
        open={openAddNewAddress}
        onClose={() => {
          setOpenAddNewAddress(false);
        }}
        position="right"
        height="100%"
        width="100%"
        sx={{
          '& .MuiDrawer-paper': {
            borderRadius: '0',
            width: '100%',
            backgroundColor: 'white',
          },
          '& .MuiBox-root': {
            padding: '0',
            maxHeight: '100vh',
          },
        }}
      >
        <MobileNav
          className="!absolute top-0 left-0 w-full  z-20 bg-[#f5f6f7]"
          titleClassName="text-center w-full text-[18px] font-bb"
          title={t('shippingAddress')}
          goBack={() => {
            setOpenAddNewAddress(false);
          }}
        />

        <div className="pt-[60px] h-full">
          <AddressForm
            data={addressFormData}
            type={actionType}
            onSubmit={handleOnSubmit}
            close={handleClose}
            // loading={loading}
          />
        </div>
      </Popup>
    </Popup>
  );
}
